<template>
	<form class="validation-form" :data-loading="loading" @submit.prevent="submit">
		<div class="form-content">
			<template v-for="item in formSchema">
				<div
					v-if="item.type === 'group'"
					:key="'group' + item.key"
					:data-form-key="item.key"
					class="group"
				>
					<div class="group-label">
						{{ item.label }}
					</div>

					<div class="group-items">
						<template v-for="childItem in item.fields">
							<component
								:is="childItem.component"
								v-if="childItem.type === 'component'"
								:key="childItem.key"
								v-bind="$attrs"
							/>

							<FormElement
								v-else
								:key="item.key + childItem.key"
								:errors="getErrors(childItem.key, v$)"
								:hide-error="bundleGroupErrors"
								:item="childItem"
								:model-value="getValueForElement(childItem)"
								@update:model-value="updateValue(childItem.key, $event)"
							/>
						</template>
					</div>

					<div v-if="bundleGroupErrors" class="group-errors">
						<FormError
							:key="`group-errors-${item.key}`"
							:errors="getGroupErrors(item, v$)"
						/>
					</div>
				</div>

				<component
					:is="item.component"
					v-else-if="item.type === 'component'"
					:key="item.key"
					v-bind="$attrs"
				/>

				<FormElement
					v-else
					:key="'element' + item.key"
					:errors="getErrors(item.key, v$)"
					:item="item"
					:model-value="getValueForElement(item)"
					@update:model-value="updateValue(item.key, $event)"
				/>
			</template>
		</div>

		<ButtonBar>
			<template #default>
				<BaseButton
					:disabled="loading"
					:text="submitButtonText"
					btn-class="primary"
					type="submit"
				/>

				<BaseButton
					v-if="showCancel"
					:disabled="loading"
					btn-class="secondary"
					text="Annuleren"
					@click="$emit('cancel')"
				/>
			</template>

			<template v-if="typeof $slots.buttonBarEnd !== 'undefined'" #end>
				<slot name="buttonBarEnd"></slot>
			</template>
		</ButtonBar>
	</form>
</template>

<script lang="ts" setup>
	import type {PropType} from 'vue';
	import {computed} from 'vue';
	import {useVuelidate} from '@vuelidate/core';
	import FormElement from '~/components/forms/FormElement.vue';
	import type {FormElementItem} from '~/lib/types/formTypes';
	import FormError from '~/components/forms/parts/FormError.vue';
	import ButtonBar from '~/components/generic/buttons/ButtonBar.vue';
	import BaseButton from '~/components/generic/buttons/BaseButton.vue';
	import useForm from '~/composables/useForm';

	const emit = defineEmits(['submit', 'cancel', 'update:modelValue']);

	const props = defineProps({
		modelValue: {
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			type: Object as PropType<Record<string, string | number | boolean | any[] | undefined | null | Record<string, any>>>,
			required: true
		},
		formSchema: {
			type: Array as PropType<FormElementItem[]>,
			required: true
		},
		loading: {
			type: Boolean,
			default: false
		},
		bundleGroupErrors: {
			type: Boolean,
			default: false
		},
		stopPropagation: {
			type: Boolean,
			default: false
		},
		submitButtonText: {
			type: String,
			default: 'Opslaan'
		},
		showCancel: {
			type: Boolean,
			default: true
		}
	});

	defineOptions({
		inheritAttrs: false
	});

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const updateValue = (key: string, value: any) => {
		emit('update:modelValue', {
			...props.modelValue,
			[key]: value
		});
	};

	const {
		getValidations,
		getErrors,
		getGroupErrors
	} = useForm();

	/**
	 * Form values need to be reactive, so we use a computed value here, before passing the value to vuelidate.
	 */
	const computedValues = computed(() => {
		return props.modelValue;
	});

	const getValueForElement = (item: FormElementItem) => {
		// @ts-expect-error - getValue should exist on UploadFormElementItem, but typing is not entirely inferred
		if (typeof item.getValue === 'function') {
			// @ts-expect-error - getValue should exist on UploadFormElementItem, but typing is not entirely inferred
			return item.getValue(props.modelValue);
		}
		return computedValues.value[item.key];
	};

	const v$ = useVuelidate(getValidations(props.formSchema), computedValues, {
		$stopPropagation: props.stopPropagation
	});

	const submit = async (event: Event) => {
		event.preventDefault();

		await v$.value.$validate();

		if (v$.value.$invalid) {
			return;
		}

		emit('submit');
	};
</script>

<style scoped>
	.form-content {
		display: grid;
		grid-gap: var(--form-grid-gap, 24px);
		container-type: inline-size;
		container-name: form;
	}

	.group,
	.group-items {
		display: grid;
		grid-gap: var(--form-grid-gap, 24px);
	}

	.group-error:empty {
		display: none;
	}

	.button-bar {
		display: flex;
		gap: var(--button-gap, 1em);
	}

	[data-loading="true"] {
		cursor: wait;

		& :deep(button[disabled]) {
			cursor: wait;
			pointer-events: none;
		}
	}
</style>
